import { useEffect, useRef } from "react";

/**
 * Custom hook to prevent interface zoom on mobile devices while allowing pinch gestures on maps
 * This prevents accidental zooming of the entire page when users pinch on non-map areas
 */
export const usePreventInterfaceZoom = () => {
  const lastTouchEnd = useRef(0);
  const touchStartTime = useRef(0);
  const initialDistance = useRef(0);

  useEffect(() => {
    const preventZoom = (e: TouchEvent) => {
      // Only prevent zoom on mobile devices
      if (window.innerWidth > 768) return;

      const now = Date.now();

      // Handle double tap zoom prevention
      if (e.type === "touchend") {
        if (now - lastTouchEnd.current <= 300) {
          e.preventDefault();
        }
        lastTouchEnd.current = now;
        return;
      }

      // Handle pinch zoom prevention
      if (e.type === "touchstart") {
        touchStartTime.current = now;

        if (e.touches.length === 2) {
          // Calculate initial distance between two fingers
          const touch1 = e.touches[0];
          const touch2 = e.touches[1];
          initialDistance.current = Math.sqrt(
            (touch2.clientX - touch1.clientX) ** 2 +
              (touch2.clientY - touch1.clientY) ** 2,
          );

          // Check if the touch is on a map element
          const target = e.target as HTMLElement;
          const isOnMap =
            target.closest("[data-map-container]") ||
            target.closest(".mapboxgl-canvas") ||
            target.closest(".cesium-widget") ||
            target.closest('[data-test-loaded="true"]');

          // If not on a map, prevent the default zoom behavior
          if (!isOnMap) {
            e.preventDefault();
          }
        }
        return;
      }

      if (e.type === "touchmove") {
        // Check if this is a pinch gesture
        if (e.touches.length === 2) {
          const target = e.target as HTMLElement;
          const isOnMap =
            target.closest("[data-map-container]") ||
            target.closest(".mapboxgl-canvas") ||
            target.closest(".cesium-widget") ||
            target.closest('[data-test-loaded="true"]');

          // If not on a map, prevent the default zoom behavior
          if (!isOnMap) {
            e.preventDefault();
          }
        }
        return;
      }
    };

    // Add event listeners with passive: false to allow preventDefault
    document.addEventListener("touchstart", preventZoom, { passive: false });
    document.addEventListener("touchmove", preventZoom, { passive: false });
    document.addEventListener("touchend", preventZoom, { passive: false });

    // Also prevent zoom via viewport meta tag manipulation
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        "content",
        "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no",
      );
    }

    return () => {
      document.removeEventListener("touchstart", preventZoom);
      document.removeEventListener("touchmove", preventZoom);
      document.removeEventListener("touchend", preventZoom);

      // Restore original viewport settings
      if (viewport) {
        viewport.setAttribute(
          "content",
          "width=device-width, initial-scale=1.0",
        );
      }
    };
  }, []);
};
